import React, {
	useEffect,
	useRef,
	useMemo,
	useState,
	useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
	resetPage,
	setAllModels,
	setSortBy,
	setStories,
} from '@/stores/slices/modelsState';
import useLadyService from '@/services/LadyService';
import './select.scss';
import {
	getUserCurrentCity,
	getUserLang,
	setCity,
	setCurrentCity,
	setLang,
} from '@/stores/slices/userSlice';
import { showFilter } from '@/stores/slices/showFilterSlice';
import { clearTitle } from '@/stores/slices/titleSlice';
import { useTranslation } from 'react-i18next';
import useCityChange from '../../../hooks/useCityChange';
import useLanguageChange from '../../../hooks/useLanguageChange';
import { useFilterLogicContext } from '@/components/filter/FilterLogicProvider';
import Icon from '../icon/Icon';
import Input from '../input/Input';

const Select = ({ ...props }) => {
	const {
		type = '',
		arrowSize,
		clazz,
		clazzText,
		clazzSvg,
		clazzWrapper,
		options,
		optionsDefault,
		name,
		size = 'l',
		subTitle = '',
		setValue = undefined,
		isSearch = false,
		searchPlaceHolder = '',
		postApi = false,
		onChange = false,
		paramsOnChange = false,
		defaultTitle = '',
		withoutIcon = false,
		hideFlag = false,
		withoutTitle = false,
		placeholder,
		showPlaceholderSubtitle = true,
		disabled,
		required,
		prefix = '',
		withIcon = false,
		controlledValue = undefined,
		defaultValue = undefined,
		controlledTitle = undefined,
		controlledIcon = undefined,
		emptyDefault = false,
		itemClazzWrapper = '',
		iconLeft = false,
		bodyClazz = '',
	} = props;

	const dispatch = useDispatch();
	const { t } = useLadyService();
	const FILTER = useFilterLogicContext();

	const { i18n } = useTranslation();
	const filterOpen = useSelector(showFilter);
	const currentCity = useSelector(getUserCurrentCity);
	const lang = useSelector(getUserLang);
	const changeCity = useCityChange();
	const changeLanguage = useLanguageChange();

	const findOption = useCallback(
		(value) => {
			if (value === undefined || value === null || !options?.length) {
				return null;
			}

			const optionValue =
				typeof value === 'object'
					? value.value ?? value.name ?? value.title
					: value;

			return (
				options.find((option) => {
					const currentOptionValue =
						option.value ?? option.name ?? option.title;
					return currentOptionValue === optionValue;
				}) ?? (typeof value === 'object' ? value : null)
			);
		},
		[options, type],
	);

	const [selected, setSelected] = useState(() =>
		findOption(controlledValue ?? defaultValue),
	);

	useEffect(() => {
		if (controlledValue !== undefined) {
			setSelected(findOption(controlledValue));
		}
	}, [controlledValue, findOption]);

	const iconNat = useMemo(
		() =>
			!!options?.length
				? options?.filter((i) => {
					return i.name === defaultTitle;
				})
				: '',
		[options, defaultTitle],
	);

	const title = useMemo(() => {
		if (controlledTitle !== undefined) return controlledTitle;

		if (selected?.title) return selected.title;

		if (name === 'language') {
			return lang.toUpperCase();
		}

		if (name === 'city') {
			return currentCity?.title ?? '';
		}

		if (defaultTitle) {
			return name === 'agencyCardsPerPage'
				? `${defaultTitle} ${t('profiles')}`
				: defaultTitle;
		}

		return emptyDefault ? '' : options?.[0]?.title ?? '';
	}, [
		controlledTitle,
		selected,
		lang,
		currentCity,
		defaultTitle,
		name,
		options,
		emptyDefault,
		t,
	]);

	const mainIcon = useMemo(() => {
		if (controlledIcon) return controlledIcon;
		if (selected?.icon) return selected.icon;
		if (name === 'city') return currentCity?.icon ?? 'EU';
		if (iconNat?.length && name === 'create-nationalist') {
			return iconNat[0].icon;
		}

		return emptyDefault ? null : options?.[0]?.icon ?? null;
	}, [
		controlledIcon,
		selected,
		currentCity,
		name,
		iconNat,
		options,
		emptyDefault,
	]);

	const [searchVal, setSearchVal] = useState('');

	const [isOpen, setIsOpen] = useState(false);

	const selectRef = useRef(null);

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleClickOutside = (event) => {
		if (selectRef.current && !selectRef.current.contains(event.target)) {
			setIsOpen(false);
			event.stopPropagation();
		}
	};

	const setValues = (item) => {
		const value = item.value ?? item.name ?? item.title ?? item;
		const title =
			emptyDefault && value === ''
				? ''
				: item.title ?? item.name ?? '';

		setSelected({
			icon: item.icon,
			title,
			value,
		});

		setValue?.(title);

		if (onChange) {
			onChange(paramsOnChange ? item : value, paramsOnChange);
		}
	};

	const handleClick = (i) => {
		setValues(i, paramsOnChange);
		if (name === 'language') {
			changeLanguage(i.value);
			i18n.changeLanguage(i.value);
			dispatch(setLang(i.value));
		}
		// if (name === 'sort') {
		// 	dispatch(setSortBy(i.value));
		// }
		if (name === 'agencyCardsPerPage') {
			setSelected({ icon: i.icon, title: +' ' + t('profiles') });
			setValues(
				{ title: i.title + ' ' + t('profiles'), value: i.value },
				paramsOnChange,
			);
		}
		if (name === 'city') {
			dispatch(resetPage());
			dispatch(setAllModels([]));
			dispatch(setStories([]));
			changeCity(i.slug);
			// change for district handling
			dispatch(
				setCurrentCity({
					id: i.id,
					title: i.title,
					icon: i.icon,
					highlight: i.highlight,
					slug: i.slug,
				}),
			);

			dispatch(setCity(i.slug));
			if (!filterOpen) {
				dispatch(setAllModels([]));
			}
			dispatch(clearTitle());
			FILTER.resetFilter(false);
		}

		setIsOpen(false);
	};

	const handleOpenSearch = (e) => {
		setIsOpen((prev) => !prev);
		const container = e.currentTarget.parentNode; // or specific selector if needed
		const searchInput = container.querySelector("input[type='search']");

		setTimeout(() => {
			if (searchInput) {
				searchInput.focus();
			} else {
				console.warn('No input present.');
			}
		}, 100);
	};

	const handleTitledIcon = () => {
		if (!!options?.length) {
			return options[0]?.tippy;
		}
	};

	const searchedData =
		searchVal && !!options?.length
			? options?.filter((item) => {
				return item.title
					?.toLowerCase()
					.includes(searchVal.toLowerCase());
			})
			: options;

	const Wrapper = useCallback(
		({ children }) => {
			return isSearch ? (
				<div className="dropdown__search-select">{children}</div>
			) : (
				children
			);
		},
		[isSearch],
	);

	return (
		<div
			className={`select${required ? ' required' : ''}${isOpen ? ' select_active' : ''
				}${clazzWrapper ? ` ${clazzWrapper}` : ''}`}
			ref={selectRef}
		>
			<button
				type={'button'}
				disabled={disabled}
				className={`select__button${clazz ? ` ${clazz}` : ''}${size ? ` size-${size}` : ''
					}${disabled ? ' __disabled' : ''}`}
				onClick={handleOpenSearch}
			>
				{withIcon && (
					<Icon
						size={'m'}
						spritePath={
							mainIcon ? mainIcon : name === 'sort' ? 'sort' : ''
						}
						clazz={`select__icon${clazzSvg ? ` ${clazzSvg}` : ''}`}
					/>
				)}

				{placeholder ? (
					title !== '' ? (
						showPlaceholderSubtitle && (
							<span className={'select__subtitle'}>
								{placeholder}
							</span>
						)
					) : (
						subTitle && (
							<span className="select__placeholder">
								{subTitle}
							</span>
						)
					)
				) : null}

				{placeholder ? (
					title === '' || title === undefined ? (
						<span className={'select__placeholder'}>
							{placeholder}
						</span>
					) : (
						<span className={'select__value'}>
							{`${prefix !== '' && title ? `${prefix} ` : ''}${title ? title : ''}`}
						</span>
					)
				) : (
					<span className={'select__value'}>
						{`${prefix !== '' && title ? `${prefix} ` : ''}${title ? title : ''}`}
					</span>
				)}

				{name === 'paymentsMethods' ? (
					<Icon
						type={'payment'}
						size={'m'}
						spritePath={mainIcon}
						clazz={`select__icon${clazzSvg ? ` ${clazzSvg}` : ''}`}
					/>
				) : null}

				{name === 'sort' && !withIcon ? (
					<Icon
						size={'m'}
						spritePath={'sort'}
						clazz={`select__icon${clazzSvg ? ` ${clazzSvg}` : ''}`}
					/>
				) : null}

				{(name === 'city' ||
					name === 'city-create' ||
					type === 'flag') &&
					(!withoutIcon || !hideFlag) ? (
					<Icon
						title={handleTitledIcon()}
						type={'flag'}
						spritePath={mainIcon ? mainIcon : 'EU'}
						clazz={`select__icon${clazzSvg ? ` ${clazzSvg}` : ''}`}
					/>
				) : null}

				{arrowSize ? (
					<Icon
						clazz={'select__arrow'}
						size={arrowSize}
						spritePath={'chevrone-down'}
					/>
				) : null}
			</button>

			<div
				className={`select__body${optionsDefault ? ` gap-8` : ''} ${bodyClazz}`}
			>
				{isSearch && (
					<label
						htmlFor={`${name}-search`}
						className="select__search"
					>
						<Input
							type="search"
							placeholder={searchPlaceHolder}
							id={`${name}-search`}
							clazz={'search__input'}
							name={`${name}-search`}
							value={searchVal}
							setValue={setSearchVal}
						/>
					</label>
				)}

				{optionsDefault && (
					<div className={'d-flex justify-sb gap-8'}>
						{optionsDefault.map((i, index) => {
							return (
								<button
									type={'button'}
									key={index + 1}
									onClick={() => handleClick(i)}
									onKeyUp={(e) => {
										if (e.key === 'Enter') handleClick(i);
									}}
									className={`select__item justify-center button--tetriary`}
								>
									{i.icon ? (
										<Icon
											type={'flag'}
											spritePath={`${i.icon}`}
											title={i?.tippy}
										/>
									) : (
										''
									)}
									{clazzText ? (
										<span className={clazzText}>
											{i.icon}
										</span>
									) : (
										i.icon
									)}
								</button>
							);
						})}
					</div>
				)}

				<Wrapper>
					{!!options?.length &&
						searchedData.map((i, index) => {
							return (
								<button
									type={'button'}
									key={index + 1}
									onClick={() => handleClick(i)}
									onKeyUp={(e) => {
										if (e.key === 'Enter') handleClick(i);
									}}
									disabled={i.disabled}
									className={`select__item ${itemClazzWrapper} ${i.disabled ? '_disabled' : ''
										}`}
								>
									{i.icon ? (
										<Icon
											type={withIcon ? '' : 'flag'}
											spritePath={`${i.icon}`}
											title={i?.tippy}
										/>
									) : (
										''
									)}
									{clazzText ? (
										<span className={clazzText}>
											{prefix !== '' ? `${prefix} ` : ''}
											{i.title || i.name}
										</span>
									) : (
										`${prefix !== '' ? `${prefix} ` : ''}${i.title || i.name
										}`
									)}
								</button>
							);
						})}
				</Wrapper>
			</div>
		</div>
	);
};

Select.propTypes = {
	options: PropTypes.array,
	currentValue: PropTypes.object,
	defaultValue: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
		PropTypes.object,
	]),
	controlledValue: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
		PropTypes.object,
	]),
	size: PropTypes.string,
	color: PropTypes.string,
	setValue: PropTypes.func,
	mainIcon: PropTypes.string,
};

export default Select;
